import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { cn, formatAmount } from '@/lib/utils'
import type { MonthlyStatistics } from '@/lib/statistics'

type Props = {
  stats: MonthlyStatistics
  /** Mean monthly total over the lookback window (excluding current month). */
  baselineTotal: number
  /** Title rendered above the figure ("今月の支出合計" etc.) */
  title: string
}

export function SummaryHero({ stats, baselineTotal, title }: Props) {
  const delta = stats.total - baselineTotal
  const pct = baselineTotal > 0 ? delta / baselineTotal : null
  const TrendIcon = pct === null || Math.abs(pct) < 0.05 ? Minus : pct > 0 ? TrendingUp : TrendingDown
  const trendClass = cn(
    'flex items-center gap-1 text-xs',
    pct === null || Math.abs(pct) < 0.05
      ? 'text-muted-foreground'
      : pct > 0
        ? 'text-destructive'
        : 'text-emerald-600',
  )

  return (
    <Card>
      <CardContent>
        <div className="text-xs text-muted-foreground">{title}</div>
        <div className="mt-1 text-3xl font-bold tabular-nums">{formatAmount(stats.total)}</div>

        {baselineTotal > 0 && (
          <div className={trendClass}>
            <TrendIcon className="h-3.5 w-3.5" />
            <span className="tabular-nums">
              通常より {pct !== null && pct >= 0 ? '+' : ''}
              {pct !== null ? `${Math.round(pct * 100)}%` : '—'}
            </span>
            <span className="text-muted-foreground tabular-nums">
              （平均 {formatAmount(baselineTotal, 'JPY', true)}）
            </span>
          </div>
        )}

        <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
          <Stat
            label="日割り平均"
            value={stats.dailyPace > 0 ? formatAmount(stats.dailyPace) : '—'}
          />
          <Stat
            label={stats.projected !== null ? '月末予測' : '取引件数'}
            value={
              stats.projected !== null
                ? formatAmount(stats.projected, 'JPY', true)
                : `${stats.transactionCount}件`
            }
          />
          <Stat
            label="支出のあった日"
            value={
              stats.spendingDays > 0
                ? `${stats.spendingDays}/${stats.daysInMonth}日`
                : '0日'
            }
          />
        </div>
      </CardContent>
    </Card>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-muted/50 px-2 py-1.5">
      <div className="text-[10px] text-muted-foreground">{label}</div>
      <div className="text-sm font-medium tabular-nums">{value}</div>
    </div>
  )
}
